import { DisposableEventAggregator } from '@repo/rpc/disposable-event-aggregator';
import {
    LocalRpcChannel,
    type MessageData,
    MockRpcChannel,
    type RpcChannel,
    UnhandledMessageError,
    WebviewContentRpcChannel,
    WindowRpcChannel,
} from '@repo/rpc/rpc';
import type { AnalyticEvents } from '@repo/types/analytic-events/analytic-events';
import {
    type CurrentTrainerInfo,
    type DeviceInfo,
    type EventDispatch,
    type GuideDataConfig,
    type HostInfo,
    HostMode,
    type HostSettings,
    HostType,
    RpcType,
    type SimpleToastMessage,
    type SwitchGameMapConfig,
    type WebviewOpenResult,
} from '@repo/types/host';
import type {
    MapCompassTarget,
    MapDataGameCoordinates,
    MapNavigationContext,
    MapSettings,
    NearbyPinNotificationData,
    TitleMapSettings,
} from '@repo/types/maps/map-types';
import type { AppSize, PreferredAppSize } from '@repo/types/shared/app';
import type { IDisposable } from '@repo/types/shared/disposable';
import type { TrainerStatus } from '@repo/types/trainers';
import { default as UAParser } from 'ua-parser-js';
import { ActionClient } from './action-client';
import type { PlayerLocationUpdate } from './player-location-types';
import type { ClientRpc } from './rpc-types';
import { VoiceClient } from './voice-client';

export class Host {
    public deviceInfo!: DeviceInfo;
    public readonly action: ActionClient;
    public readonly voice: VoiceClient;

    readonly #rpc: RpcChannel;

    readonly #events = new DisposableEventAggregator();
    #currentTrainer: CurrentTrainerInfo | null | undefined;

    public constructor(
        public readonly info: HostInfo,
        rpc: RpcChannel,
    ) {
        this.#rpc = rpc;

        document.documentElement.classList.add(`host-mode-${info.mode}`);

        this.#handleSettings(info.settings);

        this.#rpc.setHandler('external_close', (message: WebviewOpenResult) =>
            this.#handleExternalCloseMessage(message),
        );

        this.#rpc.setHandler('switch_game_map', async (config: SwitchGameMapConfig | string) => {
            if (typeof config === 'string') {
                // Maintain backwards compatibility with previous version
                this.#handleSwitchGameMap({ mapId: config });
            } else {
                this.#handleSwitchGameMap(config);
            }
        });

        this.#rpc.setHandler('set_current_trainer', async (info: CurrentTrainerInfo | null) => {
            this.#handleCurrentTrainer(info);
        });

        this.#rpc.setHandler('refresh_account', async () => this.#handleRefreshAccount());
        this.#rpc.setHandler('guide_data_update', async (guideData: GuideDataConfig) =>
            this.#handleGuideDataUpdated(guideData),
        );
        this.#rpc.setHandler('set_minimap_mode', async (params: { enable: boolean }) =>
            this.#handleSetMinimapMode(params.enable),
        );
        this.#rpc.setHandler('set_minimap_follow_facing', async (params: { enable: boolean }) =>
            this.#handleSetMinimapFollowFacing(params.enable),
        );
        this.#rpc.setHandler('player_location_update', async (message: PlayerLocationUpdate) =>
            this.#handlePlayerLocationUpdate(message),
        );
        this.#rpc.setHandler('set_player_movement_bearing', async (params: { bearing: number | null }) =>
            this.#handlePlayerMovementBearing(params.bearing),
        );
        this.#rpc.setHandler('select_pin', async (params: { externalId: string }) =>
            this.#handleSelectPin(params.externalId),
        );
        this.#rpc.setHandler('toggle_pin_found', async (params: { externalId: string }) =>
            this.#handleTogglePinFound(params.externalId),
        );

        const rpcAdapter: ClientRpc = {
            setHandler: (action, handler) => this.#rpc.setHandler(action, handler),
            removeHandler: action => this.#rpc.removeHandler(action),
            execute: (action, data) => this.#executeIgnoreUnhandled(action, data),
        };

        this.voice = new VoiceClient(rpcAdapter);
        this.action = new ActionClient(rpcAdapter);

        this.#updateDeviceInfo();
    }

    #handleExternalCloseMessage(message: WebviewOpenResult): MessageData {
        this.#events.publish('external_close', message);

        return true;
    }

    public onExternalClose(callback: (result: WebviewOpenResult) => void): IDisposable {
        return this.#events.subscribe('external_close', callback);
    }

    public async notifyRouterEvent(
        event: string,
        name: string,
        size: AppSize,
        preferredSize: PreferredAppSize,
    ): Promise<void> {
        await this.#rpc.execute('router_event', { event, name, size, preferredSize });
    }

    public async event<T extends keyof AnalyticEvents>(
        name: T,
        data: AnalyticEvents[T]['params'],
        dispatch?: EventDispatch,
    ): Promise<void> {
        await this.#rpc.execute('event', {
            name,
            data: snakeCaseObject(data),
            dispatch,
        });
    }

    public async adConversion(label: string, data: object = {}): Promise<void> {
        await this.#executeIgnoreUnhandled('ad_conversion', {
            label,
            data: snakeCaseObject(data),
        });
    }

    public async error(type: string, message?: string): Promise<void> {
        await this.#rpc.execute('error', { type, message });
    }

    public async resize(size: AppSize, preferredSize: PreferredAppSize): Promise<void> {
        await this.#rpc.execute('resize', { size, preferredSize });
    }

    public async close(sender: string, ok: boolean, output?: unknown, refreshAccount?: boolean): Promise<void> {
        await this.#rpc.execute('close', { sender, ok, output, refreshAccount });
    }

    public async viewTitle(
        titleId: string,
        gameId: string,
        previousRoute: string | null,
        parentRoute: string | null,
        location: string,
    ): Promise<void> {
        await this.#rpc.execute('view_title', { titleId, gameId, previousRoute, parentRoute, location });
    }

    public async viewCollection(slug: string): Promise<void> {
        await this.#rpc.execute('view_collection', { slug });
    }

    public async openUri(uri: string): Promise<void> {
        await this.#rpc.execute('open_uri', { uri });
    }

    public async getMapSettings(mapId: string): Promise<MapSettings | undefined> {
        return (await this.#executeIgnoreUnhandled('get_map_settings', mapId)) as MapSettings | undefined;
    }

    public async setMapSettings(mapId: string, settings: MapSettings): Promise<void> {
        await this.#executeIgnoreUnhandled('set_map_settings', { mapId, settings });
    }

    public async getTitleMapSettings(titleId: string): Promise<TitleMapSettings | undefined> {
        return (await this.#executeIgnoreUnhandled('get_title_map_settings', titleId)) as TitleMapSettings | undefined;
    }

    public async setTitleMapSettings<T extends object>(titleId: string, settings: T): Promise<void> {
        await this.#executeIgnoreUnhandled('set_title_map_settings', { titleId, settings });
    }

    public setPaymentProcessingState(processing: boolean): Promise<MessageData> {
        return this.#executeIgnoreUnhandled('set_payment_processing_state', { processing });
    }

    public toast(message: SimpleToastMessage): Promise<MessageData> {
        return this.#executeIgnoreUnhandled('toast_message', message);
    }

    public async mapEvent(event: string): Promise<void> {
        await this.#executeIgnoreUnhandled('map_event', event);
    }

    async #executeIgnoreUnhandled(
        action: string,
        data: MessageData,
        unhandledDefault?: MessageData,
    ): Promise<MessageData> {
        try {
            return await this.#rpc.execute(action, data);
        } catch (error) {
            if (error instanceof UnhandledMessageError) {
                console.warn('Unhandled message error:', error);
                return unhandledDefault;
            }
            if (this.#isTransportError(error)) {
                console.warn('RPC transport error:', error);
                return unhandledDefault;
            }
            throw error;
        }
    }

    #isTransportError(error: unknown): boolean {
        if (!(error instanceof Error)) return false;
        const msg = error.message;
        return msg.includes('EPIPE') || msg.includes('RPC channel was disposed');
    }

    #updateDeviceInfo(): void {
        const ua = new UAParser().getResult();

        this.deviceInfo = {
            os: ua.os.name === 'Windows' ? 'windows' : ua.os.name === 'Mac OS' ? 'macos' : undefined,
            type: !ua.device.type ? 'desktop' : ['tablet', 'mobile'].includes(ua.device.type) ? 'mobile' : undefined,
        };
    }

    #handleSwitchGameMap(config: SwitchGameMapConfig): void {
        this.#events.publish('switch_game_map', config);
    }

    public onSwitchGameMap(callback: (config: SwitchGameMapConfig) => void): IDisposable {
        return this.#events.subscribe('switch_game_map', callback);
    }

    #handleSetMinimapMode(enable: boolean): void {
        this.#events.publish('set_minimap_mode', enable);
    }

    public onSetMinimapMode(callback: (enable: boolean) => void): IDisposable {
        return this.#events.subscribe('set_minimap_mode', callback);
    }

    #handleSetMinimapFollowFacing(enable: boolean): void {
        this.#events.publish('set_minimap_follow_facing', enable);
    }

    public onSetMinimapFollowFacing(callback: (enable: boolean) => void): IDisposable {
        return this.#events.subscribe('set_minimap_follow_facing', callback);
    }

    #handlePlayerLocationUpdate(message: PlayerLocationUpdate): void {
        this.#events.publish('player_location_update', message);
    }

    public onPlayerLocationUpdate(callback: (message: PlayerLocationUpdate) => void): IDisposable {
        return this.#events.subscribe('player_location_update', callback);
    }

    #handlePlayerMovementBearing(bearing: number | null): void {
        this.#events.publish('set_player_movement_bearing', bearing);
    }

    public onPlayerMovementBearing(callback: (bearing: number | null) => void): IDisposable {
        return this.#events.subscribe('set_player_movement_bearing', callback);
    }

    #handleSelectPin(externalId: string): void {
        this.#events.publish('select_pin', externalId);
    }

    public onSelectPin(callback: (externalId: string) => void): IDisposable {
        return this.#events.subscribe('select_pin', callback);
    }

    #handleTogglePinFound(externalId: string): void {
        this.#events.publish('toggle_pin_found', externalId);
    }

    public onTogglePinFound(callback: (externalId: string) => void): IDisposable {
        return this.#events.subscribe('toggle_pin_found', callback);
    }

    public async getAuthToken(): Promise<string | null> {
        const result = await this.#executeIgnoreUnhandled('get_auth_token', null);
        return typeof result === 'string' ? result : null;
    }

    public async getAssistantSession(): Promise<unknown> {
        return await this.#executeIgnoreUnhandled('get_assistant_session', null);
    }

    public async getProCtaInfo(): Promise<{ isProCta: boolean; isProTrialAvailable: boolean } | null> {
        const result = await this.#executeIgnoreUnhandled('get_pro_cta_info', null);
        return result as { isProCta: boolean; isProTrialAvailable: boolean } | null;
    }

    public async triggerProCtaClick(): Promise<void> {
        await this.#executeIgnoreUnhandled('trigger_pro_cta_click', null);
    }

    public async getUserProfileInfo(): Promise<{ userProfileImageUrl?: string; userProfileName?: string } | null> {
        const result = await this.#executeIgnoreUnhandled('get_user_profile_info', null);
        return result as { userProfileImageUrl?: string; userProfileName?: string } | null;
    }

    #handleGuideDataUpdated(guideData: GuideDataConfig): void {
        this.#events.publish('guide_data_update', guideData);
    }

    public onGuideDataUpdated(callback: (guideData: GuideDataConfig) => void): IDisposable {
        return this.#events.subscribe('guide_data_update', callback);
    }

    #handleCurrentTrainer(info: CurrentTrainerInfo | null): void {
        this.#currentTrainer = info;
        this.#events.publish('current_trainer', info);
    }

    #handleRefreshAccount(): void {
        this.#events.publish('refresh_account');
    }

    public onRefreshAccount(callback: () => void): IDisposable {
        return this.#events.subscribe('refresh_account', callback);
    }

    public onCurrentTrainer(callback: (info: CurrentTrainerInfo | null) => void): IDisposable {
        const subscription = this.#events.subscribe('current_trainer', callback);
        if (this.#currentTrainer !== undefined) callback(this.#currentTrainer);
        return subscription;
    }

    public async getGameState(): Promise<Record<string, unknown> | null> {
        return (await this.#executeIgnoreUnhandled('get_game_state', null)) as Record<string, unknown> | null;
    }

    public async getTrainerStatus(): Promise<TrainerStatus | null> {
        return (await this.#executeIgnoreUnhandled('get_trainer_status', null)) as TrainerStatus | null;
    }

    public async getPlayerCoordinates(): Promise<MapDataGameCoordinates | false> {
        const result = await this.#executeIgnoreUnhandled('get_player_coordinates', null);
        return (result || false) as MapDataGameCoordinates | false;
    }

    public async setPlayerCoordinates(coords: MapDataGameCoordinates): Promise<boolean> {
        const result = await this.#executeIgnoreUnhandled('set_player_coordinates', coords);
        return !!result;
    }

    public async getAvailableMods(): Promise<unknown[] | null> {
        return (await this.#executeIgnoreUnhandled('get_available_mods', null)) as unknown[] | null;
    }

    public async setTrainerValue(config: {
        name: string;
        value: number;
        cheatId: string;
        source?: number;
    }): Promise<boolean> {
        const result = await this.#executeIgnoreUnhandled('set_trainer_value', config);
        return !!result;
    }

    public async setCurrentMapId(mapId: string): Promise<boolean> {
        return !!(await this.#executeIgnoreUnhandled('set_current_map_id', mapId));
    }

    public async sendNearbyPinNotification(data: NearbyPinNotificationData): Promise<void> {
        await this.#executeIgnoreUnhandled('nearby_pin_notification', data);
    }

    public async notifyMapNavigationContextChanged(data: MapNavigationContext): Promise<void> {
        await this.#executeIgnoreUnhandled('overlay_map_navigation_context_changed', data);
    }

    public async openOverlayMapCompass(data: MapCompassTarget): Promise<void> {
        await this.#executeIgnoreUnhandled('open_overlay_map_compass', data);
    }

    public async openMiniMap(): Promise<boolean> {
        return !!(await this.#executeIgnoreUnhandled('open_mini_map', null));
    }

    public async setVideoClipPromoDismissed(): Promise<boolean> {
        return !!(await this.#executeIgnoreUnhandled('set_video_clip_promo_dismissed', null));
    }

    public async getVideoClipPromoDismissed(): Promise<boolean> {
        return !!(await this.#executeIgnoreUnhandled('get_video_clip_promo_dismissed', null));
    }

    #handleSettings(settings: HostSettings): void {
        if (settings.theme) {
            this.#setTheme(settings.theme);
        }

        document.body.classList.toggle('override-contrast-mode', settings?.useWindowsContrastMode ?? false);
        document.body.classList.toggle('reduce-motion', settings?.reduceMotion ?? false);
    }

    #setTheme(theme: string): void {
        const validThemes = ['default', 'pro', 'purple-pro', 'green-pro', 'orange-pro'];
        const finalTheme = validThemes.includes(theme) ? theme : 'default';

        document.body.classList.add(`theme-${finalTheme}`);
    }
}

/**
 * Whether a desktop-hosted surface can obtain an auth token from its host over RPC.
 * Covers app embed, app window, and overlay embed. Web is never host-authed, and a
 * surface with no RPC channel cannot ask the host for a token.
 */
export function supportsHostAuthToken(info: { type: HostType; mode: HostMode; rpc: RpcType }): boolean {
    if (info.rpc === RpcType.NONE) {
        return false;
    }
    if (info.type === HostType.WEB) {
        return false;
    }

    return info.mode === HostMode.EMBED || (info.type === HostType.APP && info.mode === HostMode.WINDOW);
}

function createRpcChannel(info: HostInfo): RpcChannel {
    switch (info.rpc) {
        case 'webview':
            return new WebviewContentRpcChannel();
        case 'parent':
            return new WindowRpcChannel(window.parent, '*');
        case 'opener':
            return new WindowRpcChannel(window.opener, '*');
        case 'self':
            return new LocalRpcChannel({ setExecutor: () => {}, handlers: {} });
        default:
            return new MockRpcChannel();
    }
}

let _hostInstance: Host | null = null;

export function createHost(info: HostInfo): Host {
    if (_hostInstance) {
        throw new Error('Host has already been initialized');
    }
    // biome-ignore lint/suspicious/noConsole: Debug logging for host communication
    console.log(`[Host] Initializing with info:`, info);
    const rpc = createRpcChannel(info);
    _hostInstance = new Host(info, rpc);
    return _hostInstance;
}

export function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match && match[2] !== undefined ? decodeURIComponent(match[2]) : null;
}

export function getHost(): Host {
    if (!_hostInstance) {
        const urlParams = new URLSearchParams(window.location.search);
        const hostType = (urlParams.get('hostType') as HostType) ?? HostType.WEB;
        let hostMode = (urlParams.get('hostMode') as HostMode) ?? HostMode.WINDOW;
        const rpcType = (urlParams.get('rpcType') as RpcType) ?? RpcType.OPENER;

        // Auto-detect embed mode if we're in an iframe
        if (typeof window !== 'undefined' && hostType === HostType.WEB && window.self !== window.top) {
            hostMode = HostMode.EMBED;
        }

        _hostInstance = createHost({
            type: hostType,
            mode: hostMode,
            rpc: rpcType,
            settings: {
                theme: 'default',
                useWindowsContrastMode: false,
                reduceMotion: false,
            },
        });
    }
    return _hostInstance;
}

function snakeCase(value: string): string {
    const chars: string[] = [];

    for (let x = 0; x < value.length; x++) {
        const c = value.charAt(x);

        if (c >= 'A' && c <= 'Z') {
            chars.push('_', c.toLowerCase());
        } else {
            chars.push(c);
        }
    }

    return chars.join('');
}

function snakeCaseObject(data: unknown): unknown {
    if (typeof data === 'object' && data !== null) {
        return Array.isArray(data)
            ? data.map(snakeCaseObject)
            : Object.fromEntries(Object.entries(data).map(([key, value]) => [snakeCase(key), snakeCaseObject(value)]));
    } else {
        return data;
    }
}
